import { useState, useEffect } from "react";
import { BREAKPOINTS } from "../utils/constants";

/**
 * Custom hook for responsive breakpoint detection with debounced resize handling
 * @param breakpoint - Width threshold in pixels (default: BREAKPOINTS.mobile)
 * @param debounceMs - Debounce delay in milliseconds (default: 150)
 * @returns boolean indicating if viewport is below breakpoint
 */
export const useResponsive = (
  breakpoint: number = BREAKPOINTS.mobile,
  debounceMs: number = 150,
): boolean => {
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize with current window width to avoid hydration issues
    if (globalThis.window !== undefined) {
      return globalThis.window.innerWidth < breakpoint;
    }
    return false;
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      // Clear previous timeout
      clearTimeout(timeoutId);

      // Debounce the resize handler
      timeoutId = setTimeout(() => {
        setIsMobile(window.innerWidth < breakpoint);
      }, debounceMs);
    };

    // Set initial value
    setIsMobile(globalThis.window.innerWidth < breakpoint);

    // Add event listener
    globalThis.window.addEventListener("resize", handleResize, {
      passive: true,
    });

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      globalThis.window.removeEventListener("resize", handleResize);
    };
  }, [breakpoint, debounceMs]);

  return isMobile;
};

/**
 * Get current window dimensions with debounced resize handling
 * @param debounceMs - Debounce delay in milliseconds (default: 150)
 * @returns Object with width and height
 */
export const useWindowSize = (debounceMs: number = 150) => {
  const [windowSize, setWindowSize] = useState(() => {
    if (globalThis.window !== undefined) {
      return {
        width: globalThis.window.innerWidth,
        height: globalThis.window.innerHeight,
      };
    }
    return {
      width: 1200,
      height: 800,
    };
  });

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleResize = () => {
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, debounceMs);
    };

    // Set initial value
    setWindowSize({
      width: globalThis.window.innerWidth,
      height: globalThis.window.innerHeight,
    });

    globalThis.window.addEventListener("resize", handleResize, {
      passive: true,
    });

    return () => {
      clearTimeout(timeoutId);
      globalThis.window.removeEventListener("resize", handleResize);
    };
  }, [debounceMs]);

  return windowSize;
};
